import React, { Component } from 'react'
import auth from '../utils/auth'
import cfg from "../utils/config"
import axios from 'axios'
import helper from '../utils/helper'
import { saveGrantParameters, deleteGrantParameter, getGrantParameters, saveGrantSchedule, saveGrantCharts, getGrantCharts, deleteGrantCharts } from '../services/outorga'
import isEqual from "lodash/isEqual";
import { toIsoDateWithoutTime } from '../utils/normalizeDateHelpers'

const DEFAULT_STATE = {
    captationsList: [
        { label: 'Direta', value: 'direta' },
        { label: 'Barramento', value: 'barramento' },
    ],
    grantList: [],
    grantDetails: {
        basicInfo: {},
        parameters: [],
        customParameters: [],
        schedule: {},
        charts: []
    },
    isLoadingGrantList: false,
    isLoadingGrant: false,
    isLoadingGrantDefaultParams: false,
    isLoadingGrantCustomParams: false,
    isLoadingGrantSchedule: false,
    isLoadingGrantCharts: false,
    unsavedBasicInfo: null,
    unsavedDefaultParams: null,
    notAddedCustomParams: null,
    unsavedCustomParams: null,
    unsavedSchedule: null,
    unsavedCharts: null,
}

export const OutorgaContext = React.createContext(DEFAULT_STATE)

export class OutorgaContextProvider extends Component {
    userInfo = auth.getUserInfo()
    configMe = { headers: { 'Authorization': 'Bearer ' + auth.getToken() } }
    baseUrl = `${cfg.base_api_url + cfg.api_v2}/${this.userInfo.empresa}/ambiental/outorga/`

    state = {
        ...DEFAULT_STATE,
    }

    resetState = () => {
        this.setState({
            ...DEFAULT_STATE
        });
    };

    getGrantList = (state = {}) => {
        this.setState({ isLoadingGrantList: true });

        let url = `${this.baseUrl}?limit=${state?.lineCount || 10}${state.filter || ''}`;

        if (state?.url) {
            url = state.url;
        }

        return axios.get(url, this.configMe)
            .then(res => {
                this.setState({
                    grantList: res.data.results || res.data,
                    count: res.data.count || (res.data.results ? res.data.results.length : res.data.length),
                    next: res.data.next || null,
                    previous: res.data.previous || null
                });
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                this.setState({ isLoadingGrantList: false });
            });
    }

    createGrant = async (payload) => {
        this.setState({ isLoadingGrant: true });

        const body = {
            nome: payload.description,
            captacao: payload.captation,
            monitorado: payload.monitored,
            id_referencia: payload.referenceId,
            data_inicio: toIsoDateWithoutTime(payload.validity.startDate, true),
            data_vencimento: toIsoDateWithoutTime(payload.validity.endDate, true)
        };

        try {
            const res = await axios.post(this.baseUrl, body, this.configMe);

            return res;
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrant: false });
        }
    }

    getGrant = async (id) => {
        const url = `${this.baseUrl}${id}/`;
        this.setState({ isLoadingGrant: true });

        try {
            const res = await axios.get(url, this.configMe);

            this.setState(prevState => ({
                grantDetails: {
                    ...prevState.grantDetails,
                    basicInfo: res.data,
                    parameters: res.data.condicoes || [],
                    customParameters: res.data.condicoes_outros || [],
                    schedule: res.data.cronograma || {}
                }
            }));

            return res;
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrant: false });
        }
    }

    updateGrant = async (grantId, payload) => {
        this.setState({ isLoadingGrant: true });

        const body = {
            nome: payload.description,
            captacao: payload.captation,
            monitorado: payload.monitored,
            id_referencia: payload.referenceId,
            data_inicio: toIsoDateWithoutTime(payload.validity.startDate, true),
            data_vencimento: toIsoDateWithoutTime(payload.validity.endDate, true)
        };

        const url = `${this.baseUrl}${grantId}/`;

        try {
            const res = await axios.patch(url, body, this.configMe);

            if (res.status === 200) {
                this.setState(prevState => ({
                    grantDetails: {
                        ...prevState.grantDetails,
                        basicInfo: res.data,
                        parameters: res.data.condicoes || [],
                        customParameters: res.data.condicoes_outros || [],
                        schedule: res.data.cronograma || {}
                    },
                    unsavedBasicInfo: null
                }))
            }

            return res;
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrant: false });
        }
    }

    removeGrant = async (resources) => {
        if (!resources || resources.length === 0) return;

        this.setState({ isLoadingGrantList: true });

        try {
            const requests = resources.map(r => {
                const id = typeof r === 'object' ? r.id : r;
                return axios.delete(`${this.baseUrl}${id}/`, this.configMe);
            });

            await axios.all(requests);

            await this.getGrantList();
        } catch (err) {
            console.error(err);
        } finally {
            this.setState({ isLoadingGrantList: false });
        }
    }

    setContext = (value) => {
        this.setState(prevState => ({
            ...prevState,
            ...value
        }));
    }

    getGrantDetails = () => {
        return this.state.grantDetails;
    }

    saveGrantDefaultParams = async (grantId, payload) => {
        this.setState({ isLoadingGrantDefaultParams: true });

        const formatedPayload = payload.map(param => {
            return {
                id: param.id,
                nome: param.name,
                parametro_id: param.equipment_field,
                tipo: param.parameter,
                ativo: param.is_active,
                condicao: {
                    alerta: {
                        inicio: Number(param.alert_value.start),
                        fim: Number(param.alert_value.end)
                    },
                    critico: {
                        inicio: Number(param.critical_value.start),
                        fim: Number(param.critical_value.end)
                    },
                    excedido: {
                        inicio: Number(param.exceeded_value.start),
                        fim: Number(param.exceeded_value.end)
                    },
                    escala: param.escala,
                    regime: param.regime,
                    agrupamento: param.agrupamento,
                }
            }
        })

        const finalPayload = {
            condicoes: formatedPayload
        }

        try {
            const response = await saveGrantParameters(grantId, finalPayload);

            this.setState(prevState => ({
                grantDetails: {
                    ...prevState.grantDetails,
                    parameters: response.data
                }
            }))


            if (response.status === 201) {
                helper.dispatchEvent("showAviso", { message: "Parâmetros salvos com sucesso." })

                this.setUnsavedDefaultParams();
            }

            return response

        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrantDefaultParams: false });
        }
    }

    saveGrantCustomParams = async (grantId, payload) => {
        this.setState({ isLoadingGrantCustomParams: true });

        try {
            const response = await saveGrantParameters(grantId, payload);

            this.setState(prevState => ({
                grantDetails: {
                    ...prevState.grantDetails,
                    customParameters: response.data
                }
            }))

            if (response.status === 201) {
                helper.dispatchEvent("showAviso", { message: "Parâmetros salvos com sucesso." })

                this.setUnsavedCustomParams();
            }

            return response;

        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrantCustomParams: false });
        }
    };

    getGrantDefaultParameters = () => {
        return this.state.grantDetails.parameters;
    }

    getGrantCustomParameters = () => {
        return this.state.grantDetails.customParameters;
    }

    getGrantSchedule = () => {
        return this.state.grantDetails.schedule;
    }

    setUnsavedDefaultParams = (payload) => {
        if (!payload) {
            return this.setState({ unsavedDefaultParams: null });
        }
        const formatedPayload = payload
            .filter(param => param.id || param.is_active)
            .map(param => {
                return {
                    equipamento_id: param.equipment,
                    id: param.id,
                    nome: param.name,
                    parametro_id: param.equipment_field,
                    tipo: param.parameter,
                    ativo: param.is_active,
                    condicao: {
                        alerta: {
                            inicio: Number(param.alert_value.start),
                            fim: Number(param.alert_value.end)
                        },
                        critico: {
                            inicio: Number(param.critical_value.start),
                            fim: Number(param.critical_value.end)
                        },
                        excedido: {
                            inicio: Number(param.exceeded_value.start),
                            fim: Number(param.exceeded_value.end)
                        },
                        ...(param.escala && { escala: param.escala }),
                        ...(param.regime && { regime: param.regime }),
                        ...(param.agrupamento && { agrupamento: param.agrupamento }),
                    }
                }
            });

        this.setState({ unsavedDefaultParams: formatedPayload });
    }

    setUnsavedCustomParams = (payload) => {
        if (!payload) {
            return this.setState({ unsavedCustomParams: null });
        }
        const formatedPayload = payload
            .filter(param => param.id || param.ativo)
            .map(param => {
                return {
                    equipamento_id: param.equipment,
                    id: param.id,
                    nome: param.nome,
                    parametro_id: param.parametro_id,
                    tipo: param.tipo,
                    ativo: param.ativo,
                    condicao: {
                        alerta: {
                            inicio: Number(param.condicao.alerta.inicio),
                            fim: Number(param.condicao.alerta.fim)
                        },
                        critico: {
                            inicio: Number(param.condicao.critico.inicio),
                            fim: Number(param.condicao.critico.fim)
                        },
                        excedido: {
                            inicio: Number(param.condicao.excedido.inicio),
                            fim: Number(param.condicao.excedido.fim)
                        },
                        ...(param.condicao.escala && { escala: param.condicao.escala }),
                        ...(param.condicao.regime && { regime: param.condicao.regime }),
                        ...(param.condicao.agrupamento && { agrupamento: param.condicao.agrupamento }),
                    }
                }
            });

        this.setState({ unsavedCustomParams: formatedPayload });
    }

    setNotAddedCustomParams = (payload) => {
        if (!payload) {
            return this.setState({ notAddedCustomParams: null });
        }
        this.setState({ notAddedCustomParams: payload });
    }

    getHasUnsavedParams = () => {
        const { unsavedDefaultParams, unsavedCustomParams } = this.state;

        const hasUnsavedDefaultParams = !!unsavedDefaultParams && !isEqual(unsavedDefaultParams, this.state.grantDetails.parameters);
        const hasUnsavedCustomParams =
            !!unsavedCustomParams
            && !isEqual(unsavedCustomParams, this.state.grantDetails.customParameters)
            || this.state.notAddedCustomParams;

        return (hasUnsavedDefaultParams || hasUnsavedCustomParams)
    }

    handleDeleteGrantCustomParameters = async (grantId, paramId) => {
        this.setState({ isLoadingGrantCustomParams: true });

        try {
            const status = await deleteGrantParameter(grantId, paramId)

            if (status === 204) {
                const response = await getGrantParameters(grantId);

                this.setState(prevState => ({
                    grantDetails: {
                        ...prevState.grantDetails,
                        customParameters: response.data?.condicoes_outros
                    }
                }))

                helper.dispatchEvent("showAviso", { message: "Parâmetro excluído com sucesso." })
            }
        } catch (err) {
            console.error(err);

            throw err
        } finally {
            this.setState({ isLoadingGrantCustomParams: false });
        }
    }

    handleSaveGrantSchedule = async (grantId, payload) => {
        this.setState({ isLoadingGrantSchedule: true });

        try {
            const response = await saveGrantSchedule(grantId, payload);

            if (response.status === 200) {
                this.setState(prevState => ({
                    grantDetails: {
                        ...prevState.grantDetails,
                        schedule: response.data?.cronograma
                    },
                    unsavedSchedule: null
                }))

                helper.dispatchEvent("showAviso", { message: "Cronograma salvo com sucesso." })
            }

            return response;

        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrantSchedule: false });
        }
    };

    handleSaveGrantCharts = async (grantId, payload) => {
        this.setState({ isLoadingGrantCharts: true });

        try {
            const response = await saveGrantCharts(grantId, payload);
            const data = response?.data;

            if (response.status === 200) {
                this.setState(prevState => ({
                    grantDetails: {
                        ...prevState.grantDetails,
                        charts: data.map(chart => ({
                            id: chart.id,
                            parametro_principal: chart.parametro_principal,
                            parametros_mesclados: chart.parametros_mesclados
                        }))
                    },
                    unsavedCharts: null
                }))

                helper.dispatchEvent("showAviso", { message: "Gráficos salvos com sucesso." })
            }

            return response;

        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrantCharts: false });
        }
    };

    handleGetGrantCharts = async (grantId) => {
        this.setState({ isLoadingGrantCharts: true });

        try {
            const res = await getGrantCharts(grantId);
            const data = res?.data?.results;

            if (res.status === 200) {
                this.setState(prevState => ({
                    grantDetails: {
                        ...prevState.grantDetails,
                        charts: data.map(chart => ({
                            id: chart.id,
                            parametro_principal: chart.parametro_principal,
                            parametros_mesclados: chart.parametros_mesclados
                        }))
                    }
                }));
            }

            return res;
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrantCharts: false });
        }
    }

    handleDeleteGrantCharts = async (grantId, chartId) => {
        this.setState({ isLoadingGrantCharts: true });

        try {
            await deleteGrantCharts(grantId, chartId);

            this.handleGetGrantCharts(grantId);

            helper.dispatchEvent("showAviso", { message: "Gráfico excluído com sucesso." })
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            this.setState({ isLoadingGrantCharts: false });
        }
    }

    unsavedBasicInfo = (value) => {
        this.setState({
            unsavedBasicInfo: {
                ...this.state.grantDetails.basicInfo,
                ...value
            }
        });
    }

    setUnsavedSchedule = (value) => {
        if (!value) {
            return this.setState({ unsavedSchedule: null });
        }
        this.setState({
            unsavedSchedule: {
                ...this.state.unsavedSchedule,
                ...value
            }
        });
    }

    setUnsavedCharts = (value) => {
        if (!value || isEqual(value, this.state?.grantDetails?.charts)) {
            return this.setState({ unsavedCharts: null });
        }
        this.setState({ unsavedCharts: value });
    }

    hasUnsavedBasicInfo = () => {
        return !!this.state.unsavedBasicInfo && !isEqual(this.state.grantDetails.basicInfo, this.state.unsavedBasicInfo);
    }

    hasUnsavedSchedule = () => {
        return !!this.state.unsavedSchedule && !isEqual(this.state.grantDetails.schedule, this.state.unsavedSchedule);
    }

    hasUnsavedCharts = () => {
        return !!this.state.unsavedCharts && !isEqual(this.state.grantDetails.charts, this.state.unsavedCharts);
    }

    render() {
        const value = {
            ...this.state,
            resetState: this.resetState,
            createGrant: this.createGrant,
            getGrant: this.getGrant,
            updateGrant: this.updateGrant,
            getGrantList: this.getGrantList,
            removeGrant: this.removeGrant,
            set: this.setContext,
            getGrantDetails: this.getGrantDetails,
            saveGrantDefaultParams: this.saveGrantDefaultParams,
            getParameters: this.getGrantDefaultParameters,
            getCustomParameters: this.getGrantCustomParameters,
            getGrantSchedule: this.getGrantSchedule,
            saveGrantCustomParams: this.saveGrantCustomParams,
            setUnsavedDefaultParams: this.setUnsavedDefaultParams,
            setUnsavedCustomParams: this.setUnsavedCustomParams,
            setNotAddedCustomParams: this.setNotAddedCustomParams,
            hasUnsavedParams: this.getHasUnsavedParams,
            getCustomParameters: this.getGrantCustomParameters,
            deleteGrantParameters: this.handleDeleteGrantCustomParameters,
            handleSaveGrantSchedule: this.handleSaveGrantSchedule,
            handleSaveGrantCharts: this.handleSaveGrantCharts,
            handleGetGrantCharts: this.handleGetGrantCharts,
            handleDeleteGrantCharts: this.handleDeleteGrantCharts,
            unsavedBasicInfo: this.unsavedBasicInfo,
            hasUnsavedBasicInfo: this.hasUnsavedBasicInfo,
            setUnsavedSchedule: this.setUnsavedSchedule,
            hasUnsavedSchedule: this.hasUnsavedSchedule,
            setUnsavedCharts: this.setUnsavedCharts,
            hasUnsavedCharts: this.hasUnsavedCharts,
        }

        return (
            <OutorgaContext.Provider value={value}>
                {this.props.children}
            </OutorgaContext.Provider>
        )
    }
}